/**
 * Turning the registration screen's codes into words.
 *
 * Nothing here holds state and nothing renders, matching `notes/noteDisplay.ts`: the screen reads
 * these, so the mapping from a refusal code to a message is testable without a DOM and the
 * wording is reviewable in one place.
 *
 * **Every code gets a message.** {@link registrationRefusalMessage} is exhaustive over
 * {@link RegistrationRefusalCode} by construction, so a code added on the server and typed into
 * `REGISTRATION_REFUSAL_CODES` fails the typecheck here until somebody has written the wording.
 * A default branch would be a blank message on the one screen where a clinician is holding a
 * wristband and cannot get any further.
 */

import type { TFunction } from 'i18next'

import type {
  RegistrationRefusalCode,
  RegistrationRefusalParams,
} from './registrationApi'
import type { DraftProblem, RegistrationField } from './registrationDraft'

/**
 * The lookup the registration screen needs.
 *
 * `common` first, matching the other display modules. Four namespaces, and two of them are here
 * for reasons worth stating.
 *
 * **`patient` is in the list on purpose**, for the reason `noteDisplay.ts` gives about SOAP
 * headings: `Reservnummer`, `Kön`, `Född`, `Avdelning` and `Vårdplats` already exist there
 * because the overview renders them, and the box a clinician types a value into must be labelled
 * with the same word the value is read under. Two catalogue entries holding `Vårdplats` with
 * nothing keeping them equal is a label that drifts the first time a reviewer rewords one.
 *
 * **`auth` is in the list** so the withheld-form case can use `mayNotDocumentMessage` — the same
 * wording a refused write produces, since `ROLE_MAY_NOT_DOCUMENT` is the same code either way.
 */
export type RegistrationTranslate = TFunction<
  ['common', 'auth', 'patient', 'registration']
>

/** A field's label, reused as the name in a "too long" message. */
export function registrationFieldLabel(
  field: RegistrationField,
  t: RegistrationTranslate,
): string {
  switch (field) {
    case 'identifier':
      // The record's own word for the number. The form cannot accept a personnummer, so the
      // label says which kind of number belongs in the box rather than "Identifierare".
      return t('patient:identifier.reservnummer')
    case 'assigner':
      return t('registration:field.assigner')
    case 'givenName':
      return t('registration:field.givenName')
    case 'familyName':
      return t('registration:field.familyName')
    case 'birthDate':
      return t('patient:overview.birthDate')
    case 'bed':
      return t('patient:overview.location')
  }
}

/**
 * The ward dropdown's label (#47).
 *
 * Not in {@link registrationFieldLabel}, because the ward is not a {@link RegistrationField}: it
 * lives beside the draft rather than in it, since the ward list and this form share one selection.
 * Here for the same reason the rest are — so every word on the screen is looked up in one module —
 * and it is `patient:overview.ward` for the reason given above about `Vårdplats`.
 */
export function registrationWardLabel(t: RegistrationTranslate): string {
  return t('patient:overview.ward')
}

/**
 * The sentence under a field, saying what belongs in it.
 *
 * Registration-only, so these are `registration` keys throughout. They carry more weight here
 * than on the note editor: this screen is used when a patient arrives without an identity, which
 * is the least rehearsed thing in the demo, and the hint under the identifier field is where the
 * "off a pre-printed band, not generated here" rule is actually stated to the person doing it
 * (DECISIONS.md #40).
 */
export function registrationFieldHint(
  field: RegistrationField,
  t: RegistrationTranslate,
): string {
  switch (field) {
    case 'identifier':
      return t('registration:fieldHint.identifier')
    case 'assigner':
      return t('registration:fieldHint.assigner')
    case 'givenName':
      return t('registration:fieldHint.givenName')
    case 'familyName':
      return t('registration:fieldHint.familyName')
    case 'birthDate':
      return t('registration:fieldHint.birthDate')
    case 'bed':
      return t('registration:fieldHint.bed')
  }
}

/**
 * A field name as it arrived in a `REGISTRATION_FIELD_TOO_LONG` param, in words.
 *
 * Falls back to the raw name rather than dropping it, matching `noteDisplay.ts`: the message's
 * job is to say which box to shorten, and a server field this interface has not met is still
 * better named than not named.
 */
function fieldNameFromParams(
  field: string | undefined,
  t: RegistrationTranslate,
): string {
  switch (field) {
    case 'identifier':
    case 'assigner':
    case 'givenName':
    case 'familyName':
    case 'birthDate':
    case 'bed':
      return registrationFieldLabel(field, t)
    default:
      return field ?? ''
  }
}

/**
 * What to tell the clinician about a refusal.
 *
 * The `UNKNOWN` case is not a fallback for laziness — it is what a proxy error page or a future
 * code reaches, and its message says the patient was **not** registered, which is the one fact
 * that matters and is true of every refusal.
 */
export function registrationRefusalMessage(
  code: RegistrationRefusalCode | 'UNKNOWN',
  params: RegistrationRefusalParams,
  t: RegistrationTranslate,
): string {
  switch (code) {
    case 'REGISTRATION_IDENTIFIER_MISSING':
      return t('registration:refusal.identifierMissing')

    case 'REGISTRATION_IDENTIFIER_IS_NATIONAL_NUMBER':
      // The one refusal whose remedy is a different screen. A clinician who typed a personnummer is
      // usually looking for a patient who came in with the imported record, so the message sends
      // them to the search — and says what to do if the search finds nobody. It must not *state*
      // that the patient is in the record: nothing looked, because looking would mean sending the
      // number, and this refusal exists so that it is never sent. Until 2026-10-08 it said
      // "finns redan i journalen" for a number that was in no record (found in testing).
      return t('registration:refusal.identifierIsNationalNumber')

    case 'REGISTRATION_IDENTIFIER_MALFORMED':
      // The server's lengths, not the client constants, so a drift between the two shows up as a
      // disagreement a reader can see rather than as a wrong number in a message.
      return t('registration:refusal.identifierMalformed', {
        letters: params.prefixLength ?? 0,
        digits: params.sequenceLength ?? 0,
      })

    case 'REGISTRATION_IDENTIFIER_IN_USE':
      // Not an error in the form: the number is filed, so there is a patient to open. This is
      // also what a retry after an uncertain outcome lands on, which is why the wording says the
      // number is already in the record rather than that something went wrong.
      return t('registration:refusal.identifierInUse', {
        identifier: params.identifier ?? '',
      })

    case 'REGISTRATION_ASSIGNER_MISSING':
      return t('registration:refusal.assignerMissing')

    case 'REGISTRATION_FAMILY_NAME_MISSING':
      // Names the way out, because there is one and it is the whole point of the field: a patient
      // with no name is registered by marking the identity as an assumption (AGENTS.md §6).
      return t('registration:refusal.familyNameMissing')

    case 'REGISTRATION_FIELD_TOO_LONG':
      return t('registration:refusal.fieldTooLong', {
        field: fieldNameFromParams(params.field, t),
        maximum: params.max ?? 0,
      })

    case 'REGISTRATION_SEX_UNKNOWN':
      return t('registration:refusal.sexUnknown')

    case 'REGISTRATION_BIRTH_DATE_MALFORMED':
      return t('registration:refusal.birthDateMalformed')

    case 'REGISTRATION_BIRTH_DATE_IN_FUTURE':
      return t('registration:refusal.birthDateInFuture')

    case 'REGISTRATION_WARD_MISSING':
      return t('registration:refusal.wardMissing')

    case 'WARD_NOT_FOUND':
      // A fact about the record rather than about the form: the ward the screen was showing is
      // not in `clinical.ward`. Named, because on a box with several wards the code is what the
      // technician needs to hear.
      return t('registration:refusal.wardNotFound', { ward: params.ward ?? '' })

    case 'ROLE_MAY_NOT_DOCUMENT':
      // Deliberately not `auth:refusal.roleMayNotDocument` with the role from `params.roleCode`.
      // The screen already knows the signed-in role from the session and uses
      // `mayNotDocumentMessage` for the withheld form; reaching this branch means the form was
      // offered and the write was refused anyway, which is a state the message should describe as
      // the registration not having happened.
      return t('registration:refusal.roleMayNotDocument')

    case 'REGISTRATION_BODY_MISSING':
    case 'UNKNOWN':
      return t('registration:refusal.unknown')
  }
}

/**
 * What to tell the clinician when the registration could not be attempted, or answered something
 * unusable.
 *
 * Separate wording from every refusal, because the action is opposite: a refusal is something to
 * fix in the form, and this is something to wait out — so the message says to leave the form
 * open. The typed details are nowhere else, and a patient who has just arrived is standing there.
 */
export function registrationUnavailableMessage(t: RegistrationTranslate): string {
  return t('registration:refusal.unavailable')
}

/** The same, for a problem this side found before sending. */
export function draftProblemMessage(
  problem: DraftProblem,
  t: RegistrationTranslate,
): string {
  // One function for both, deliberately: the client's checks mirror the server's, so a mistake
  // reads the same whether the form caught it or the box did. Two wordings for one mistake is how
  // a clinician ends up unable to tell which check they failed.
  return registrationRefusalMessage(problem.code, problem.params, t)
}

/**
 * A sex in words, for the form's select.
 *
 * The overview's keys, for the reason {@link RegistrationTranslate} gives: the option a clinician
 * picks and the word their patient's header then carries must be the same. This is
 * `patientOverviewDisplay.patientSex` with a different `t` type and nothing else — not worth
 * sharing across the two modules, worth keeping on the same catalogue keys.
 */
export function offeredSexLabel(sex: string, t: RegistrationTranslate): string {
  switch (sex) {
    case 'female':
      return t('patient:sex.female')
    case 'male':
      return t('patient:sex.male')
    case 'other':
      return t('patient:sex.other')
    case 'unknown':
      // `unknown` is a documented answer — somebody established that it is not known — and is
      // offered alongside leaving the field empty, which is nobody having answered.
      return t('patient:sex.unknown')
    default:
      // The vocabulary is closed by migration 0003's CHECK, so this is unreachable from
      // `OFFERED_SEXES`. Showing the code beats hiding the option, the same fallback
      // `patientSex` makes when reading.
      return sex
  }
}

/** The empty choice in the sex select: nobody has answered, which is not the same as `unknown`. */
export function sexNotAnsweredLabel(t: RegistrationTranslate): string {
  return t('registration:sex.notAnswered')
}
